import React, { useEffect, useRef, useState } from 'react';
import { Alert, Input, Space, Typography } from 'antd';
import { TeamOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useApporteursApi, type TVerificationCode } from '../../api/paiement/apporteurs';
import { libelleAvantage } from '../../pages/apporteur/avantage';

type Props = {
  value: string;
  onChange: (value: string) => void;
  /** Signale le statut connu du code (pour un affichage voisin, facultatif). */
  onStatut?: (statut: TVerificationCode['statut'] | null) => void;
  disabled?: boolean;
};

/**
 * Champ « code animateur », facultatif, distinct du code de l'école.
 *
 * Le code est vérifié à la volée (route publique, débit limité) pour dire au
 * parent ce qu'il vaut. Un code inconnu ou inactif ne bloque RIEN : le
 * parcours continue sans attribution ni avantage, le message le dit.
 *
 * La mention d'information est toujours affichée à côté du champ : l'animateur
 * verra le nom du parent, celui de ses enfants et leur école.
 */
const CodeApporteurField: React.FC<Props> = ({ value, onChange, onStatut, disabled }) => {
  const { t } = useTranslation();
  const api = useApporteursApi();
  // api change d'identité à chaque rendu : on passe par une ref.
  const apiRef = useRef(api);
  apiRef.current = api;
  const onStatutRef = useRef(onStatut);
  onStatutRef.current = onStatut;
  const [verification, setVerification] = useState<TVerificationCode | null>(null);
  // Numéro de la demande en cours : une réponse tardive ne doit pas écraser
  // celle d'un code saisi après elle.
  const demande = useRef(0);

  useEffect(() => {
    const code = value.trim();
    const numero = ++demande.current;
    if (!code) {
      setVerification(null);
      onStatutRef.current?.(null);
      return;
    }
    const minuteur = window.setTimeout(() => {
      apiRef.current
        .verifierCode(code)
        .then((v) => {
          if (numero !== demande.current) return;
          setVerification(v);
          onStatutRef.current?.(v.statut);
        })
        .catch(() => {
          // Vérification indisponible : on n'affirme rien, le serveur jugera
          // le code au moment de l'inscription ou du paiement.
          if (numero !== demande.current) return;
          setVerification(null);
          onStatutRef.current?.(null);
        });
    }, 500);
    return () => window.clearTimeout(minuteur);
  }, [value]);

  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      <Typography.Text style={{ fontWeight: 500 }}>
        {t('parrainage.champ.label', 'Code animateur (facultatif)')}
      </Typography.Text>
      <Input
        size="large"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        prefix={<TeamOutlined />}
        placeholder={t('parrainage.champ.placeholder', 'Par exemple KARIM2026')}
        autoComplete="off"
        allowClear
      />
      {verification?.statut === 'valide' && (
        <Alert
          type="success"
          showIcon
          message={t('parrainage.champ.valide', 'Code animateur reconnu')}
          description={libelleAvantage(t, verification.avantage)}
        />
      )}
      {verification?.statut === 'inactif' && (
        <Alert
          type="warning"
          showIcon
          message={t('parrainage.champ.inactif', "Ce code n'est plus actif : vous pouvez continuer sans code.")}
        />
      )}
      {(verification?.statut === 'inconnu' || verification?.statut === 'invalide') && (
        <Alert
          type="warning"
          showIcon
          message={t('parrainage.champ.inconnu', 'Code inconnu : vérifiez-le, ou continuez sans code.')}
        />
      )}
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {t(
          'parrainage.champ.mention',
          "En utilisant le code d'un animateur, vous l'informez de votre inscription : il verra votre nom, celui de vos enfants et leur école."
        )}
      </Typography.Text>
    </Space>
  );
};

export default CodeApporteurField;
